import './core/setup/setupYup.ts';
import './core/setup/setupAuth.ts';
import './core/setup/setupImmer.ts';
// Locale
import './core/dates/initDayjs.ts';
import './core/setup/setupIntl.ts';
// Styles
import 'antd/dist/reset.css';
import { apiClient } from '@framework/api-client/client/apiClient.ts';
import { joinPath } from '@framework/api-client/utils/joinPath.ts';
import { refreshTokenInterceptor } from '@framework/auth/http/refreshTokenInterceptor.ts';
import { initCommonWebWidgetRepository } from '@framework/common-web/engine/extendWidgetRepository.ts';
import { DocumentsInlineViewerContextProvider } from '@framework/common-web/ui/documents/DocumentsInlineViewerContext.tsx';
import { DocumentsInlineViewerDrawer } from '@framework/common-web/ui/documents/DocumentsInlineViewerDrawer.tsx';
import { initWidgetRepository } from '@framework/engine/schema/widgets/repository/initWidgetRepository.tsx';
import { EngineThemeProvider } from '@framework/engine/ui/theme/EngineThemeProvider.tsx';
import { I18nConfigProvider } from '@framework/i18n/components/I18nConfigProvider.tsx';
import { ErrorBoundaryRoutes } from '@framework/ui/components/alert/ErrorBoundaryRoutes.tsx';
import { ButtonTooltipsGlobalStyle } from '@framework/ui/components/button/ButtonTooltipsGlobalStyle.tsx';
import { MessageGlobalConfiguration } from '@framework/ui/components/messages/MessageGlobalConfiguration.tsx';
import { BreadcrumbProvider } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { AntThemeProvider } from '@framework/ui/theme/AntThemeProvider.tsx';
import { LayoutGlobalStyle } from '@framework/ui/theme/LayoutGlobalStyle.tsx';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { frontofficeDevToolsPlugins } from '#features/apps/frontoffice-web/src/dev-tools/frontofficeDevToolsPlugins.tsx';
import { DevToolsProvider } from '#features/packages/common-web/src/dev-tools/DevToolsProvider.tsx';
import { DevToolsWidget } from '#features/packages/common-web/src/dev-tools/DevToolsWidget.tsx';
import { initXsdConverterWidgetRepository } from '#features/packages/xsd-converter/src/widgets/initXsdConverterWidgetRepository.ts';
import { initXsdWebWidgetRepository } from '#features/packages/xsd-web/src/initXsdWebWidgetRepository.ts';
import {
  FontGlobalStyle,
  ThemeGlobalStyle,
  ThemeRootProvider,
  theme,
  themeComponentTokens,
  themeTokens,
} from '#theme';
import { appConfig } from './core/appConfig.ts';
import { queryClient } from './core/network/queryClient.ts';
import { store } from './core/store/store.ts';
import { Routes } from './routes/Routes.tsx';

// Configuro il client delle API. Inserire in file dedicato.
refreshTokenInterceptor(apiClient, store);
apiClient.defaults.baseURL = joinPath([appConfig.apiBaseUrl]);

initWidgetRepository();
initCommonWebWidgetRepository();
initXsdConverterWidgetRepository();
initXsdWebWidgetRepository();

export function App() {
  return (
    <BrowserRouter basename={appConfig.rootNavigationPath}>
      <MessageGlobalConfiguration />
      <Provider store={store}>
        <I18nConfigProvider
          theme={{
            token: themeTokens,
            components: themeComponentTokens,
          }}
        >
          <AntThemeProvider theme={theme}>
            <HelmetProvider>
              {/* Importazione GlobalStyle */}
              <ButtonTooltipsGlobalStyle />
              <LayoutGlobalStyle />
              <FontGlobalStyle />
              <ThemeGlobalStyle />
              <EngineThemeProvider accentColor={themeTokens.colorPrimary}>
                <QueryClientProvider client={queryClient}>
                  <ThemeRootProvider>
                    <DocumentsInlineViewerContextProvider isEnabled>
                      {theme.enablePdfDrawer && <DocumentsInlineViewerDrawer />}
                      <DevToolsProvider>
                        <BreadcrumbProvider>
                          <ErrorBoundaryRoutes
                            rootNavigationPath={appConfig.rootNavigationPath}
                          >
                            <Routes />
                          </ErrorBoundaryRoutes>
                        </BreadcrumbProvider>
                        <DevToolsWidget plugins={frontofficeDevToolsPlugins} />
                      </DevToolsProvider>
                    </DocumentsInlineViewerContextProvider>
                  </ThemeRootProvider>
                </QueryClientProvider>
              </EngineThemeProvider>
            </HelmetProvider>
          </AntThemeProvider>
        </I18nConfigProvider>
      </Provider>
    </BrowserRouter>
  );
}

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container!);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
